import { scheduleLazyScript } from "~/layers/base/utils/scheduleLazyScript";

const DOTDIGITAL_SCRIPT_ID = "dotdigital-ddgtag";
// Public Dotdigital tracking id (like a GTM / Clarity id), not a secret.
// Overridable per environment via NORCE_DOTDIGITAL_TAG_ID / _REGION; this
// hardcoded id stays as the backward-compatible default for B&B deploys
// that predate the env vars.
const DEFAULT_TAG_ID = "DM-0206374401-01";
const DEFAULT_REGION = "r1";

type CookiebotWindow = Window & {
	Cookiebot?: { consent?: { marketing?: boolean } };
};

/**
 * Dotdigital (ddgtag) web-behaviour tracking — lazy + consent-gated.
 *
 * - Lazy: deferred via the shared `scheduleLazyScript` helper (same
 *   load → idle deferral as the Clarity plugin) so it stays off the
 *   critical render path.
 * - Consent: this is marketing tracking, so it loads only after the
 *   visitor accepts the Cookiebot `marketing` category (AGENTS rule #4).
 *   A single handler reacts to every consent signal — first accept,
 *   stored-consent replay on return visits, AND revocation (decline
 *   removes the script). Both inject and remove are DOM-idempotent, so
 *   repeated events are harmless and re-consent works.
 * - Injected at the end of <body> per Dotdigital's snippet.
 *
 * No SRI: `ddgtag.js` is an unversioned, mutable CDN entrypoint, so a
 * pinned `integrity` hash would break on Dotdigital's next deploy.
 */
export default defineNuxtPlugin(() => {
	const w = window as CookiebotWindow;

	const { dotdigital } = useRuntimeConfig().public as {
		dotdigital?: { tagId?: string; region?: string };
	};
	const tagId = dotdigital?.tagId || DEFAULT_TAG_ID;
	const region = dotdigital?.region || DEFAULT_REGION;
	const scriptSrc = `https://${region}.ddlnk.net/${tagId}/ddgtag.js`;

	const hasMarketingConsent = () => Boolean(w.Cookiebot?.consent?.marketing);

	const injectScript = () => {
		// Re-check at inject time, not just when scheduling: consent can be
		// withdrawn during the lazy delay (accept → decline within the
		// ~1.5s + idle window), and the scheduled task can't be cancelled.
		if (!hasMarketingConsent()) {
			return;
		}
		if (document.getElementById(DOTDIGITAL_SCRIPT_ID)) {
			return;
		}
		const script = document.createElement("script");
		script.id = DOTDIGITAL_SCRIPT_ID;
		script.src = scriptSrc;
		script.async = true;
		document.body.appendChild(script);
	};

	const removeScript = () => {
		document.getElementById(DOTDIGITAL_SCRIPT_ID)?.remove();
	};

	// One handler for every consent signal: schedule the script when
	// marketing consent is present, remove it when it's withdrawn.
	const syncWithConsent = () => {
		if (hasMarketingConsent()) {
			scheduleLazyScript(injectScript);
		} else {
			removeScript();
		}
	};

	window.addEventListener("CookiebotOnAccept", syncWithConsent);
	window.addEventListener("CookiebotOnConsentReady", syncWithConsent);
	window.addEventListener("CookiebotOnDecline", syncWithConsent);

	// Consent may already be resolved before this plugin runs.
	if (document.readyState === "complete") {
		syncWithConsent();
	} else {
		window.addEventListener("load", syncWithConsent, { once: true });
	}

	// Dev/HMR: drop listeners when this module is hot-replaced so they
	// don't accumulate across reloads. No-op (tree-shaken) in production.
	if (import.meta.hot) {
		import.meta.hot.dispose(() => {
			window.removeEventListener("CookiebotOnAccept", syncWithConsent);
			window.removeEventListener("CookiebotOnConsentReady", syncWithConsent);
			window.removeEventListener("CookiebotOnDecline", syncWithConsent);
			window.removeEventListener("load", syncWithConsent);
		});
	}
});
